import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { LineMask, FadeIn, easeExpo } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { services } from "@/data/services";

const HERO_IMG = "/media/abstract-architectural-curves.jpg";

const audiences = [
  "Local Authority Youth Justice Services",
  "Children's Services",
  "Regional partnerships",
  "Voluntary & Community Sector organisations",
  "Commissioning organisations",
  "Consultancy partners",
];

const outcomes = [
  { number: "01", title: "Children", text: "Practice that keeps children safe and supports them to thrive." },
  { number: "02", title: "Victims", text: "Work that recognises harm and prioritises victims' voices." },
  { number: "03", title: "Communities", text: "Safer communities through effective, accountable services." },
];

const principles = [
  { number: "01", slug: "independent-challenge", title: "Independent challenge", text: "External scrutiny and honest challenge, free from organisational bias — a perspective you and your board can trust." },
  { number: "02", slug: "practical-recommendations", title: "Practical recommendations", text: "Clear, achievable actions that support continuous improvement — not reports that gather dust." },
  { number: "03", slug: "evidence-informed-solutions", title: "Evidence-informed solutions", text: "Advice grounded in research, inspection frameworks and what is known to work in youth justice." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      <section
        ref={heroRef}
        className="noise-overlay relative min-h-screen flex flex-col justify-end overflow-hidden bg-ink"
        data-testid="home-hero"
      >
        <motion.div
          style={{ y: bgY }}
          className="absolute inset-0 will-change-transform"
          data-testid="home-hero-background"
        >
          <img
            src={HERO_IMG}
            alt="Abstract architectural curves"
            className="h-[120%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-ink/60" />
          <div className="absolute inset-0 bg-brand/15 mix-blend-multiply" />
        </motion.div>

        <motion.div
          style={{ opacity: fade }}
          className="relative z-10 mx-auto w-full max-w-[1600px] px-6 lg:px-12 pb-16 pt-44"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-mono text-[11px] sm:text-xs tracking-[0.24em] uppercase text-paper/60"
            data-testid="hero-overline"
          >
            Specialist Youth Justice Consultancy · England &amp; Wales
          </motion.p>

          <h1 className="mt-8 font-display font-semibold text-paper text-5xl sm:text-7xl lg:text-8xl leading-[0.98] tracking-tight">
            <LineMask
              delay={0.35}
              lines={[
                "Independent insight.",
                <em key="l2" className="text-brand">Practical improvement.</em>,
              ]}
            />
          </h1>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05, ease: easeExpo }}
              className="md:col-span-5 text-base sm:text-lg leading-relaxed text-paper/75"
              data-testid="hero-subcopy"
            >
              KC Consultancy helps Youth Justice Services identify strengths, address
              risks and implement practical, evidence-informed improvements — for
              children, victims and communities.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2, ease: easeExpo }}
              className="md:col-span-7 flex flex-wrap gap-4 md:justify-end"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 text-sm tracking-wide text-paper transition-colors duration-300 hover:bg-paper hover:text-ink"
              >
                Contact Us
                <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="inline-flex items-center gap-2 rounded-full border border-paper/40 px-8 py-4 text-sm tracking-wide text-paper transition-colors duration-300 hover:border-paper hover:bg-paper/10"
              >
                Explore services
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-16 flex items-center justify-between border-t border-paper/15 pt-6"
          >
            <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-paper/45">
              Quality · Governance · Improvement
            </p>
            <p className="hidden sm:flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-paper/45">
              Scroll
              <motion.span
                animate={{ y: [0, 5, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </motion.span>
            </p>
          </motion.div>
        </motion.div>
      </section>

      <section className="bg-paper py-28 lg:py-40" data-testid="home-intro">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <FadeIn>
              <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                The consultancy
              </p>
            </FadeIn>
          </div>
          <div className="md:col-span-8 md:col-start-5">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink">
              <LineMask
                inView
                lines={[
                  "Independent scrutiny should do more",
                  "than identify issues — it should leave",
                  "services stronger than it found them.",
                ]}
              />
            </h2>
            <FadeIn delay={0.35} className="mt-10 max-w-2xl">
              <p className="text-base sm:text-lg leading-relaxed text-ink/65">
                KC Consultancy draws on extensive experience in youth justice,
                management, quality assurance and performance improvement — working
                alongside local authorities and partner organisations to deliver
                practical solutions, credible advice and measurable impact.
              </p>
              <Link
                to="/about"
                data-testid="intro-about-link"
                className="link-underline mt-8 inline-flex items-center gap-2 text-sm font-medium text-ink"
              >
                Our approach
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      <section className="bg-paper py-28 lg:py-40" data-testid="home-services">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 pb-14">
            <div>
              <FadeIn>
                <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                  What we do
                </p>
              </FadeIn>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl text-ink">
                <LineMask inView lines={["Seven specialist services."]} />
              </h2>
            </div>
            <FadeIn delay={0.2}>
              <Link
                to="/services"
                data-testid="services-view-all-link"
                className="link-underline inline-flex items-center gap-2 text-sm font-medium text-ink"
              >
                View all services
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>

          <div className="border-t border-ink/10">
            {services.map((service, i) => (
              <FadeIn key={service.id} delay={i * 0.05}>
                <Link
                  to={`/services#${service.id}`}
                  data-testid={`home-service-${service.id}`}
                  className="group grid grid-cols-12 items-center gap-4 border-b border-ink/10 py-8 lg:py-10 px-2 -mx-2 transition-colors duration-300 hover:bg-cream"
                >
                  <span className="col-span-2 md:col-span-1 font-mono text-sm text-slateblue">
                    {service.number}
                  </span>
                  <span className="col-span-10 md:col-span-4 font-display text-2xl lg:text-3xl text-ink">
                    {service.title}
                  </span>
                  <span className="col-span-10 col-start-3 md:col-span-5 md:col-start-6 text-sm leading-relaxed text-ink/55">
                    {service.summary}
                  </span>
                  <span className="hidden md:flex col-span-2 justify-end">
                    <ArrowUpRight className="h-6 w-6 text-ink/30 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-brand" />
                  </span>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper py-28 lg:py-40" data-testid="home-who-we-work-with">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <FadeIn>
              <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                Who we work with
              </p>
            </FadeIn>
            <h2 className="mt-6 font-display font-semibold text-4xl sm:text-5xl leading-tight text-ink">
              <LineMask inView lines={["Supporting the whole", "youth justice landscape."]} />
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-slateblue mb-6">
              We support
            </p>
            <ul className="border-t border-ink/10">
              {audiences.map((audience, i) => (
                <FadeIn key={audience} delay={i * 0.05}>
                  <li
                    className="flex items-baseline gap-6 border-b border-ink/10 py-5"
                    data-testid={`audience-${i + 1}`}
                  >
                    <span className="font-mono text-sm text-slateblue">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-xl sm:text-2xl text-ink">{audience}</span>
                  </li>
                </FadeIn>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="noise-overlay bg-ink py-28 lg:py-40 text-paper" data-testid="home-outcomes">
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-16">
            <div className="md:col-span-3">
              <FadeIn>
                <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                  Why it matters
                </p>
              </FadeIn>
            </div>
            <h2 className="md:col-span-8 md:col-start-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
              <LineMask inView lines={["Better outcomes", "are the only measure."]} />
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
            {outcomes.map((item, i) => (
              <FadeIn key={item.number} delay={i * 0.08}>
                <div
                  className="border-l border-paper/15 pl-8 h-full"
                  data-testid={`outcome-${item.title.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <p className="font-mono text-sm text-brand">{item.number}</p>
                  <h3 className="mt-6 font-display text-2xl">{item.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-paper/60">{item.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-28 lg:py-40" data-testid="home-approach">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-3">
              <FadeIn>
                <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                  Our approach
                </p>
              </FadeIn>
            </div>
            <div className="md:col-span-8 md:col-start-5">
              <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink">
                <LineMask
                  inView
                  lines={["Every commission is tailored", "to the needs of the organisation."]}
                />
              </h2>
              <FadeIn delay={0.3} className="mt-8 max-w-2xl">
                <p className="text-base sm:text-lg leading-relaxed text-ink/65">
                  We work collaboratively with leaders and practitioners to provide
                  independent challenge, practical recommendations and evidence-informed
                  solutions that strengthen practice and support continuous improvement —
                  with Child First principles and the voice of the child at the centre.
                </p>
              </FadeIn>
            </div>
          </div>
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
            {principles.map((p, i) => (
              <FadeIn key={p.number} delay={i * 0.1}>
                <div className="border-t-2 border-ink pt-8" data-testid={`principle-${p.slug}`}>
                  <p className="font-mono text-sm text-slateblue">{p.number}</p>
                  <h3 className="mt-4 font-display text-2xl sm:text-3xl text-ink">{p.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-ink/60">{p.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper py-28 lg:py-40" data-testid="home-cta">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <h2 className="md:col-span-8 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-tight text-ink">
            <LineMask
              inView
              lines={["Need independent Youth Justice", "quality assurance or inspection", "readiness support?"]}
            />
          </h2>
          <FadeIn delay={0.25} className="md:col-span-4 md:justify-self-end">
            <p className="text-sm leading-relaxed text-ink/60 mb-8">
              Contact KC Consultancy to discuss your requirements and arrange an
              initial conversation.
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact-button"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-sm tracking-wide text-paper transition-colors duration-300 hover:bg-brand"
            >
              Contact Us
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
